import { Button, TableColumnsType, Tooltip, message } from 'antd';
import ScrollTable, { ScrollTableProps } from 'components/ScrollTable';
import StatusSwitch from 'components/StatusSwitch';
import StatusButton from 'components/StatusButton';
import { DefaultTag } from 'views/Aide/Scenario/TaskTable';
import { useTranslate } from '@tolgee/react';
import { deleteTask, updateTask } from 'views/Todo/service';
import { useCallback } from 'react';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import PopoverPreview from 'components/StoreScopes/PopoverPreview';

type RT = Todo.Task;

export interface TaskTableProps extends ScrollTableProps<RT> {
  onCheckDetail: (record: RT) => void;
  onUpdated: () => any;
}

function TaskTable(props: TaskTableProps) {
  const { onCheckDetail, onUpdated, ...otherProps } = props;

  const { whiteListConfig: { AIDE_REMOVE_TASK } = {} } = useOrgConfigStateValue();

  const { t, getTaskName } = useGetTaskName();

  const columns: TableColumnsType<RT> = [
    {
      key: 'name',
      title: AIDE_REMOVE_TASK ? t('marketing.策略名称') : t('marketing.任务名称'),
      width: 150,
      render: (_, record) => {
        const { isDefault, name } = record;

        if (!isDefault) {
          return name;
        }

        return (
          <div>
            {getTaskName(record)}
            <Tooltip
              title={t(
                'aide.新授权店铺会自动添加至默认任务中，当店铺从默认任务或其他任务中移除时，不会再自动加入默认任务中',
              )}
            >
              <DefaultTag color="blue">{t('marketing.默认')}</DefaultTag>
            </Tooltip>
          </div>
        );
      },
    },
    {
      key: 'storeList',
      dataIndex: 'storeList',
      title: t('aide.应用店铺'),
      width: 120,
      render: (storeList: RT['storeList']) => <PopoverPreview value={storeList} />,
    },
    {
      key: 'enable',
      dataIndex: 'enable',
      title: AIDE_REMOVE_TASK ? t('aide.策略开关') : t('aide.任务开关'),
      width: 100,
      render: (_, { enable, ...others }) => {
        return (
          <StatusSwitch
            checkedChildren={t('aide.开')}
            unCheckedChildren={t('aide.关')}
            checked={enable}
            onChange={(enable) => {
              return updateTask({
                enable,
                ...others,
              })
                .then(() => {
                  message.success(t('operation.saveSucceed'));

                  return onUpdated();
                })
                .catch(() => {
                  message.error(t('operation.saveFailed'));
                });
            }}
          />
        );
      },
    },
    {
      key: 'operation',
      title: t('操作'),
      width: 120,
      render: (_, record) => {
        const { id, isDefault } = record;

        return (
          <div>
            <Button type="link" onClick={() => onCheckDetail(record)} style={{ paddingLeft: 0 }}>
              {t('tableColumn.查看详情')}
            </Button>
            {!isDefault && (
              <StatusButton
                type="link"
                popConfirmProps={{
                  placement: 'topRight',
                  title: AIDE_REMOVE_TASK
                    ? t('marketing.您确定删除该策略吗？')
                    : t('marketing.你确定要删除当前任务吗'),
                  okText: t('marketing.删除'),
                  cancelText: t('marketing.取消'),
                }}
                onClick={() => {
                  return deleteTask(id)
                    .then(() => {
                      message.success(t('phrase.删除成功'));

                      return onUpdated();
                    })
                    .catch(() => {
                      message.error(t('phrase.删除失败，请稍后再试'));
                    });
                }}
              >
                {t('marketing.删除')}
              </StatusButton>
            )}
          </div>
        );
      },
    },
  ];

  return <ScrollTable rowKey="id" columns={columns} {...otherProps} />;
}

export function useGetTaskName() {
  const { t } = useTranslate();

  const getTaskName = useCallback(
    ({ isDefault, name }: Pick<Todo.Task, 'isDefault' | 'name'>) => {
      if (!isDefault) {
        return name;
      }

      return name === 'default' ? t('todo.待办') : name;
    },
    [t],
  );

  return {
    t,
    getTaskName,
  };
}

export default TaskTable;
